import { useCallback, useEffect, useRef, useState } from 'react';
import {
  unreadNotificationCount as customerUnreadCount,
  markAllNotificationsRead as customerMarkAllRead,
  markNotificationRead as customerMarkRead,
  deleteNotification as customerDeleteNotification,
} from './customer-api';
import {
  unreadNotificationCount as proUnreadCount,
  markAllNotificationsRead as proMarkAllRead,
  markNotificationRead as proMarkRead,
  deleteNotification as proDeleteNotification,
} from './professional-api';
import { isCustomerAuthenticated } from './customer-auth';
import { isAuthenticated as isProAuthenticated } from './auth';

type Role = 'customer' | 'professional';

const POLL_INTERVAL_MS = 60_000;

// Cross-component signal: marking notifications read in one place (e.g.
// the inbox list) needs to refresh the unread count everywhere it's
// shown (e.g. the header bell), even though each useUnreadNotifications
// instance holds its own count state. We broadcast a window event and
// every live hook re-fetches the count API.
const NOTIFICATIONS_CHANGED_EVENT = 'cherry:notifications-changed';

/** Tell every mounted bell/inbox to re-fetch the unread count. */
export function notifyNotificationsChanged() {
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

/**
 * Lightweight polling hook for the header bell. Returns the unread
 * count for the requested role plus a `refresh()` you can call after
 * marking-as-read so the badge updates immediately without waiting
 * for the next poll. Skips network work when the user isn't signed
 * in for the requested role.
 *
 * Refresh on `visibilitychange` so coming back to the tab gives a
 * fresh count without the user having to navigate.
 */
export function useUnreadNotifications(role: Role) {
  const [count, setCount] = useState(0);
  const mountedRef = useRef(true);

  const fetcher = role === 'customer' ? customerUnreadCount : proUnreadCount;
  const isAuthed =
    role === 'customer' ? isCustomerAuthenticated() : isProAuthenticated();

  const refresh = useCallback(async () => {
    if (!isAuthed) {
      if (mountedRef.current) setCount(0);
      return;
    }
    try {
      const res = await fetcher();
      if (mountedRef.current) setCount(res?.unread_count ?? 0);
    } catch {
      // Stale count is better than a UI explosion — swallow.
    }
  }, [fetcher, isAuthed]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();

    const interval = window.setInterval(refresh, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Re-fetch when any component signals a read/mark-all so the badge
    // stays in sync across the bell and the inbox.
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    };
  }, [refresh]);

  return { count, refresh };
}

/**
 * Mark-as-read helpers that pick the right axios instance based on
 * role. The inbox page calls these on row click. Returning the new
 * unread count would require a follow-up GET; we instead let the
 * caller invoke `refresh()` from useUnreadNotifications.
 */
export async function markNotificationRead(role: Role, id: string) {
  const res =
    role === 'customer' ? await customerMarkRead(id) : await proMarkRead(id);
  // Broadcast so the header bell (a separate hook instance) re-fetches.
  notifyNotificationsChanged();
  return res;
}

export async function markAllNotificationsRead(role: Role) {
  const res =
    role === 'customer'
      ? await customerMarkAllRead()
      : await proMarkAllRead();
  notifyNotificationsChanged();
  return res;
}

export async function deleteNotification(role: Role, id: string) {
  const res =
    role === 'customer'
      ? await customerDeleteNotification(id)
      : await proDeleteNotification(id);
  notifyNotificationsChanged();
  return res;
}
